import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, RotateCcw, Save } from 'lucide-react';
import { api } from '../../api/client';
import { SkeletonPage } from '../../components/Skeleton';
import { toast } from '../../lib/toast';

// Admin → Emails. Edit the subject + HTML body of the 7 transactional purchase
// emails. Defaults live in the backend (services/mailer/templates.js); saving
// stores an override, "Reset" deletes it. The token reference panel lists the
// {{placeholders}} each email supports; the live preview renders with sample
// data through the same engine production uses.
export default function EmailTemplatesPage() {
  const qc = useQueryClient();
  const [selectedKey, setSelectedKey] = useState(null);
  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [preview, setPreview] = useState({ subject: '', html: '' });
  const htmlRef = useRef(null);

  const listQuery = useQuery({
    queryKey: ['admin-email-templates'],
    queryFn: () => api.get('/api/admin/email-templates').then((r) => r.data.templates),
  });

  const templates = listQuery.data;
  const selected = useMemo(
    () => templates?.find((t) => t.key === selectedKey) || null,
    [templates, selectedKey],
  );

  // Auto-select the first template once loaded.
  useEffect(() => {
    if (!selectedKey && templates?.length) setSelectedKey(templates[0].key);
  }, [templates, selectedKey]);

  // Load the selected template's current copy (override if present, else default)
  // into the editor whenever the selection or underlying data changes.
  useEffect(() => {
    if (!selected) return;
    setSubject(selected.override?.subject ?? selected.default.subject);
    setHtmlBody(selected.override?.htmlBody ?? selected.default.html);
  }, [selected]);

  // Debounced live preview from the backend (so it matches production rendering).
  useEffect(() => {
    if (!selected) return;
    const handle = setTimeout(() => {
      api
        .post(`/api/admin/email-templates/${selected.key}/preview`, { subject, htmlBody })
        .then((r) => setPreview(r.data))
        .catch(() => {});
    }, 350);
    return () => clearTimeout(handle);
  }, [selected, subject, htmlBody]);

  const saveMutation = useMutation({
    mutationFn: () =>
      api
        .put(`/api/admin/email-templates/${selected.key}`, { subject, htmlBody })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-email-templates'] });
      toast.success('Email template saved');
    },
    onError: (err) => toast.error(err, 'Could not save template'),
  });

  const resetMutation = useMutation({
    mutationFn: () =>
      api.post(`/api/admin/email-templates/${selected.key}/reset`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-email-templates'] });
      toast.success('Reverted to default');
    },
    onError: (err) => toast.error(err, 'Could not reset template'),
  });

  const insertToken = (token) => {
    const el = htmlRef.current;
    const snippet = `{{${token}}}`;
    if (!el) {
      setHtmlBody((b) => b + snippet);
      return;
    }
    const start = el.selectionStart ?? htmlBody.length;
    const end = el.selectionEnd ?? htmlBody.length;
    const next = htmlBody.slice(0, start) + snippet + htmlBody.slice(end);
    setHtmlBody(next);
    // Restore caret just after the inserted token.
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + snippet.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const isDirty =
    selected &&
    (subject !== (selected.override?.subject ?? selected.default.subject) ||
      htmlBody !== (selected.override?.htmlBody ?? selected.default.html));

  if (listQuery.isLoading) return <SkeletonPage />;
  if (listQuery.isError) {
    return <div className="card text-sm text-accent-red">Could not load email templates.</div>;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
      {/* Template list */}
      <div className="card h-fit">
        <div className="section-header">
          <div className="section-title">
            <span className="section-icon">
              <Mail className="w-4 h-4" />
            </span>
            Emails
          </div>
        </div>
        <ul className="space-y-1">
          {templates.map((t) => (
            <li key={t.key}>
              <button
                type="button"
                onClick={() => setSelectedKey(t.key)}
                className={
                  'w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ' +
                  (t.key === selectedKey
                    ? 'bg-brand-50 text-brand-700 font-medium'
                    : 'text-ink-700 hover:bg-gray-50')
                }
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate">{t.label}</span>
                  {t.isOverridden && (
                    <span className="pill bg-amber-100 text-amber-700 flex-none">Edited</span>
                  )}
                </div>
                <div className="text-[11px] text-ink-400 mt-0.5">{t.group}</div>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Editor */}
      {selected && (
        <div className="space-y-6 min-w-0">
          <div className="card">
            <div className="section-header flex-wrap gap-3">
              <div className="section-title">
                <span className="section-icon">
                  <Mail className="w-4 h-4" />
                </span>
                {selected.label}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={!selected.isOverridden || resetMutation.isPending}
                  onClick={() => resetMutation.mutate()}
                  title={
                    selected.isOverridden
                      ? 'Delete the override and revert to the built-in default'
                      : 'Already using the default'
                  }
                >
                  <RotateCcw className="w-4 h-4" />
                  {resetMutation.isPending ? 'Resetting…' : 'Reset to default'}
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={!isDirty || saveMutation.isPending}
                  onClick={() => saveMutation.mutate()}
                >
                  <Save className="w-4 h-4" />
                  {saveMutation.isPending ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>

            <p className="text-sm text-ink-500 mb-4">{selected.description}</p>

            <div className="space-y-4">
              <div>
                <label className="label">Subject</label>
                <input
                  className="input"
                  value={subject}
                  maxLength={200}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </div>
              <div>
                <label className="label">HTML body</label>
                <textarea
                  ref={htmlRef}
                  className="input font-mono text-[13px] leading-relaxed"
                  rows={14}
                  value={htmlBody}
                  onChange={(e) => setHtmlBody(e.target.value)}
                />
              </div>

              {/* Tokens */}
              <div>
                <label className="label">Available tokens — click to insert</label>
                <div className="flex flex-wrap gap-2">
                  {selected.tokens.map((tk) => (
                    <button
                      key={tk.token}
                      type="button"
                      onClick={() => insertToken(tk.token)}
                      title={tk.label}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-gray-200 bg-gray-50 hover:bg-brand-50 hover:border-brand-200 transition-colors"
                    >
                      <code className="text-[12px] text-brand-700">{`{{${tk.token}}}`}</code>
                      <span className="text-[11px] text-ink-400 hidden sm:inline">{tk.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Live preview */}
          <div className="card">
            <div className="section-header">
              <div className="section-title">
                <span className="section-icon">
                  <Mail className="w-4 h-4" />
                </span>
                Preview
              </div>
              <span className="text-xs text-ink-400">rendered with sample data</span>
            </div>
            <div className="rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                <div className="text-xs text-ink-400">Subject</div>
                <div className="text-sm font-semibold text-ink-900">{preview.subject}</div>
              </div>
              <iframe
                title="email-preview"
                className="w-full bg-white"
                style={{ height: 360, border: 0 }}
                sandbox=""
                srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><style>body{font:15px/1.6 -apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#1d2433;margin:0;padding:20px}a{color:#2f6fed}code{background:#f1f3f8;padding:2px 6px;border-radius:6px}pre{background:#f7f7f8;padding:12px;border-radius:8px;white-space:pre-wrap}</style></head><body>${preview.html || ''}</body></html>`}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
